import { HomeyAPI, type HomeyAPIV2 } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { hasStatusCode } from '../../../lib/errors';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

type LocationOption = Awaited<ReturnType<HomeyAPI['geolocation']['getOptionLocation']>>;
type ModeOption = Awaited<ReturnType<HomeyAPIV2.ManagerGeolocation['getOptionMode']>>;

export interface State {
  optionLocationData: LocationOption | null;
  optionModeData: ModeOption | null;
  isLoading: boolean;
  error: unknown;
}

export type LocationSettingsStoreState = State;

export class LocationSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('location-settings');
  }

  override createInitialState() {
    return {
      optionLocationData: null,
      optionModeData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      const optionLocationPromise = api.geolocation.getOptionLocation();
      const optionModePromise = LocationSettingsStore.getOptionMode(api);

      const [optionLocationData, optionModeData] = await Promise.all([
        optionLocationPromise,
        optionModePromise,
      ]);

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        optionLocationData,
        optionModeData: optionModeData,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, this.key);

    return { locationSettings: this.store(selectAll, shallow) };
  }

  async setOptionLocation({ value }: { value: HomeyAPIV2.GeolocationLocation | null }) {
    const api = this.getApi();
    const result = await api.geolocation.setOptionLocation({
      value: value == null ? null : { ...value },
    });
    this.set({ optionLocationData: result });
  }

  async setOptionMode({ value }: ModeOption) {
    const api = this.getApi();

    if (HomeyAPI.isHomeyAPIV2(api) === false) {
      throw new Error('Automatic location is only supported on Homey API V2');
    }

    const result = await api.geolocation.setOptionMode({ value });
    this.set({ optionModeData: result });
  }

  static async getOptionMode(api: HomeyAPI): Promise<ModeOption | null> {
    if (HomeyAPI.isHomeyAPIV2(api) === false) {
      return null;
    }

    try {
      return await api.geolocation.getOptionMode();
    } catch (error) {
      if (hasStatusCode(error) && error.statusCode === 404) {
        return null;
      }

      if (typeof error === 'object' && error !== null && 'code' in error && error.code === 404) {
        return null;
      }

      throw error;
    }
  }
}

function selectAll(state: LocationSettingsStoreState) {
  return {
    ...selectBase(state),
    optionLocationData: state.optionLocationData,
    optionModeData: state.optionModeData,
  };
}

export const locationSettingsStore = new LocationSettingsStore();
